import { rd } from '@passionware/monads';
import { ReactNode } from 'react';
import { DropdownMenuSeparator } from 'v5/design-system/DropdownMenu';
import { ThemePreferenceSection } from 'v5/features/core/ThemeSwitcher';
import { cn } from 'v5/platform/dom/cn';
import { PortalContainerScope } from 'v5/primitives/PortalContainer';
import { WithServices } from 'v5/platform/ts/services';
import { WithFeatureService } from 'v5/services/front/FeatureService/FeatureService';
import { WithContentThemeService } from 'v5/services/front/ThemeService/ThemeService';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import styles from './contentTheme.module.scss';

type ContentThemeServices = WithServices<
  [WithFeatureService, WithAppScopeService, WithContentThemeService]
>;

function useContentThemeEnabled({ services }: ContentThemeServices) {
  return rd.getOrElse(
    services.featureService.useFeature(
      services.appScopeService.useResolvedScopeIdentity(),
      'content-theme'
    ),
    false
  );
}

/**
 * Class for the element that holds the page content: the content theme's
 * `dark` and a text colour from the tokens. `undefined` with the
 * `content-theme` flag off.
 */
export function useContentThemeClassName(props: ContentThemeServices) {
  const enabled = useContentThemeEnabled(props);
  const theme = props.services.contentThemeService.useEffectiveTheme();
  return enabled ? cn(styles.content, theme === 'dark' && 'dark') : undefined;
}

/**
 * The content theme for `children` and for the overlays they open (dialogs,
 * menus, tooltips portal into a container with the same classes).
 */
export function ContentThemeScope({
  children,
  ...props
}: ContentThemeServices & { children: ReactNode }) {
  const className = useContentThemeClassName(props);
  return className === undefined ? (
    children
  ) : (
    <div className={cn(styles.scope, className)}>
      <PortalContainerScope className={className}>
        {children}
      </PortalContainerScope>
    </div>
  );
}

/** The theme menu's Content theme section, with the `content-theme` flag on. */
export function ContentThemeMenuSection(props: ContentThemeServices) {
  if (!useContentThemeEnabled(props)) return null;
  return (
    <>
      <DropdownMenuSeparator />
      <ThemePreferenceSection
        label="Content theme"
        themeService={props.services.contentThemeService}
      />
    </>
  );
}
